'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import { UserProfileManager } from '@/lib/user-profile-manager';
import { RecommendationEngine } from '@/lib/recommendation-engine';

interface PersonalizedDeal {
  id: string;
  type: 'flight' | 'itinerary';
  title: string;
  destination: string;
  description: string;
  price: number;
  originalPrice?: number;
  image: string;
  alt: string;
  highlights: string[];
  confidence: number;
  aiGenerated: boolean;
  ctaText: string;
  ctaLink: string;
}

interface PersonalizedDealsProps {
  className?: string;
}

const PersonalizedDeals = ({ className = '' }: PersonalizedDealsProps) => {
  const [deals, setDeals] = useState<PersonalizedDeal[]>([]);
  const [loading, setLoading] = useState(true);
  const [personalizationScore, setPersonalizationScore] = useState(0);

  useEffect(() => {
    generatePersonalizedDeals();
  }, []);

  const generatePersonalizedDeals = async () => {
    try {
      setLoading(true);

      // Get user profile
      const profile = UserProfileManager.getProfile();
      const score = RecommendationEngine.calculatePersonalizationScore(profile);
      setPersonalizationScore(score);

      // Generate AI-powered deals based on user profile
      const generatedDeals = await fetchClaudeRecommendations(profile);

      setDeals(generatedDeals);
    } catch (error) {
      console.error('Error generating personalized deals:', error);
      // Fallback to default deals
      setDeals(getDefaultDeals());
    } finally {
      setLoading(false);
    }
  };

  const fetchClaudeRecommendations = async (profile: any): Promise<PersonalizedDeal[]> => {
    try {
      // Build context from user profile
      const context = profile ? UserProfileManager.getPersonalizationContext(profile) : '';

      const response = await fetch('/api/anthropic/travel-recommendations', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          searchParams: {
            from: profile?.bookingPatterns?.frequentDestinations?.[0]?.destination || 'London',
            to: 'Worldwide',
            departDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            passengers: { adults: 1, children: 0, infants: 0 },
            cabinClass: profile?.preferences?.preferredCabinClass?.[0] || 'economy'
          },
          userProfile: profile,
          userPreferences: 'Generate 4 personalized flight deals and itineraries for the homepage'
        })
      });

      if (!response.ok) {
        // Silently fall back to default deals if API is not configured
        return getDefaultDeals();
      }

      const data = await response.json();

      // Parse Claude response into deals
      return parseClaudeResponseToDeals(data, profile);
    } catch (error) {
      // Silently fall back to default deals
      return getDefaultDeals();
    }
  };

  const parseClaudeResponseToDeals = (data: any, profile: any): PersonalizedDeal[] => {
    const deals: PersonalizedDeal[] = [];

    // Extract itinerary suggestions from Claude response
    if (data.data?.itinerarySuggestions) {
      data.data.itinerarySuggestions.slice(0, 2).forEach((itinerary: any, index: number) => {
        deals.push({
          id: `ai-itinerary-${index}`,
          type: 'itinerary',
          title: itinerary.title || 'Personalized Itinerary',
          destination: itinerary.activities?.[0]?.split(' ')?.[0] || 'Worldwide',
          description: itinerary.activities?.[0] || 'Curated experiences just for you',
          price: parseInt(itinerary.estimatedCost?.match(/\d+/)?.[0] || '500'),
          image: getImageForDestination(index),
          alt: `Personalized ${itinerary.title} itinerary with curated activities`,
          highlights: itinerary.activities?.slice(0, 3) || ['Personalized activities', 'Local experiences', 'Expert recommendations'],
          confidence: 0.85,
          aiGenerated: true,
          ctaText: 'View Itinerary',
          ctaLink: '/itinerary-gallery'
        });
      });
    }

    // Extract flight recommendations
    if (data.data?.personalizedSuggestions) {
      data.data.personalizedSuggestions.slice(0, 2).forEach((suggestion: any, index: number) => {
        if (suggestion.type === 'flight') {
          // Use deterministic prices to avoid hydration mismatch
          const staticPrices = [349, 449, 299, 529];
          const staticOriginalPrices = [549, 699, 449, 749];
          deals.push({
            id: `ai-flight-${index}`,
            type: 'flight',
            title: suggestion.title,
            destination: profile?.bookingPatterns?.frequentDestinations?.[index]?.destination || 'Popular Destination',
            description: suggestion.description,
            price: staticPrices[index % staticPrices.length],
            originalPrice: staticOriginalPrices[index % staticOriginalPrices.length],
            image: getImageForDestination(index + 2),
            alt: `AI-recommended flight deal to ${profile?.bookingPatterns?.frequentDestinations?.[index]?.destination || 'destination'}`,
            highlights: [suggestion.reasoning, 'AI-optimized timing', 'Best price guarantee'],
            confidence: suggestion.confidence,
            aiGenerated: true,
            ctaText: 'Search Flights',
            ctaLink: '/flight-search'
          });
        }
      });
    }

    // Fill remaining slots with intelligent defaults
    while (deals.length < 4) {
      deals.push(...getDefaultDeals().slice(deals.length, 4));
    }

    return deals.slice(0, 4);
  };

  const getImageForDestination = (index: number): string => {
    const images = [
    "https://images.unsplash.com/photo-1552220348-65caf44b4954",
    "https://images.unsplash.com/photo-1696519669474-3001c0e2b548",
    "https://images.unsplash.com/photo-1542137052-0d8f95e46514",
    "https://images.unsplash.com/photo-1712851665026-0624ac2baaee"];

    return images[index % images.length];
  };

  const getDefaultDeals = (): PersonalizedDeal[] => {
    return [
    {
      id: 'default-1',
      type: 'flight',
      title: 'Weekend Getaway Special',
      destination: 'Paris',
      description: 'Perfect for a quick European escape',
      price: 299,
      originalPrice: 450,
      image: "https://images.unsplash.com/photo-1552220348-65caf44b4954",
      alt: 'Eiffel Tower illuminated at sunset with Seine River in foreground',
      highlights: ['Direct flights', 'Flexible dates', 'City center hotels'],
      confidence: 0.7,
      aiGenerated: false,
      ctaText: 'Book Now',
      ctaLink: '/flight-search?to=Paris'
    },
    {
      id: 'default-2',
      type: 'itinerary',
      title: 'Mediterranean Discovery',
      destination: 'Greece & Italy',
      description: 'Island hopping adventure with cultural immersion',
      price: 1899,
      image: "https://images.unsplash.com/photo-1696519669474-3001c0e2b548",
      alt: 'White-washed Greek island buildings overlooking crystal blue Mediterranean waters',
      highlights: ['10 days', 'All-inclusive', 'Private tours'],
      confidence: 0.75,
      aiGenerated: false,
      ctaText: 'Explore',
      ctaLink: '/itinerary-gallery'
    },
    {
      id: 'default-3',
      type: 'flight',
      title: 'Business Class Upgrade',
      destination: 'New York',
      description: 'Premium comfort for transatlantic travel',
      price: 1599,
      originalPrice: 2200,
      image: "https://images.unsplash.com/photo-1590985716937-5ac6685ca4a4",
      alt: 'New York City skyline at dusk with Empire State Building prominently featured',
      highlights: ['Lie-flat seats', 'Lounge access', 'Priority boarding'],
      confidence: 0.8,
      aiGenerated: false,
      ctaText: 'View Details',
      ctaLink: '/flight-search?to=New York&class=business'
    },
    {
      id: 'default-4',
      type: 'itinerary',
      title: 'Asian Culinary Journey',
      destination: 'Thailand & Japan',
      description: 'Food lover\'s paradise with cooking classes',
      price: 2499,
      image: "https://images.unsplash.com/photo-1712851665026-0624ac2baaee",
      alt: 'Traditional Japanese temple with cherry blossoms and Mount Fuji in background',
      highlights: ['14 days', 'Cooking classes', 'Market tours'],
      confidence: 0.72,
      aiGenerated: false,
      ctaText: 'Learn More',
      ctaLink: '/itinerary-gallery'
    }];

  };

  if (loading) {
    return (
      <section className={`py-16 lg:py-24 bg-gradient-to-b from-surface to-background ${className}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="h-10 bg-muted animate-pulse rounded-lg w-96 mx-auto mb-4" />
            <div className="h-6 bg-muted animate-pulse rounded-lg w-[600px] mx-auto" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map((i) =>
            <div key={i} className="bg-card rounded-xl shadow-brand overflow-hidden">
                <div className="h-48 bg-muted animate-pulse" />
                <div className="p-5 space-y-3">
                  <div className="h-6 bg-muted animate-pulse rounded" />
                  <div className="h-4 bg-muted animate-pulse rounded w-3/4" />
                  <div className="h-4 bg-muted animate-pulse rounded w-1/2" />
                </div>
              </div>
            )}
          </div>
        </div>
      </section>);

  }

  return (
    <section className={`py-16 lg:py-24 bg-gradient-to-b from-surface to-background ${className}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground">
              Personalized For You
            </h2>
            <div className="flex items-center gap-2 px-4 py-2 bg-primary/10 rounded-full">
              <Icon name="SparklesIcon" size={20} className="text-primary" />
              <span className="text-sm font-semibold text-primary">AI-Powered</span>
            </div>
          </div>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Claude-curated flight deals and itineraries tailored to your travel preferences
            {personalizationScore > 0 &&
            <span className="block mt-2 text-primary font-semibold">
                {personalizationScore}% Personalized Based on Your Profile
              </span>
            }
          </p>
        </div>

        {/* Deals Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {deals.map((deal) =>
          <div
            key={deal.id}
            className="bg-card rounded-xl shadow-brand hover:shadow-brand-lg transition-all duration-300 overflow-hidden group relative">

              {/* AI Badge */}
              {deal.aiGenerated &&
            <div className="absolute top-3 left-3 z-10 bg-gradient-to-r from-primary to-secondary text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center gap-1 shadow-lg">
                  <Icon name="SparklesIcon" size={12} />
                  <span>AI Curated</span>
                </div>
            }

              {/* Discount Badge */}
              {deal.originalPrice &&
            <div className="absolute top-3 right-3 z-10 bg-coral text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg">
                  Save £{deal.originalPrice - deal.price}
                </div>
            }

              {/* Image */}
              <div className="relative h-48 overflow-hidden">
                <AppImage
                src={deal.image}
                alt={deal.alt}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />

                <div className="absolute inset-0 bg-gradient-to-t from-charcoal/60 to-transparent" />
                
                {/* Type Badge */}
                <div className="absolute bottom-3 left-3">
                  <span className="px-3 py-1 bg-white/90 backdrop-blur-sm text-charcoal rounded-full text-xs font-semibold capitalize">
                    {deal.type}
                  </span>
                </div>
              </div>

              {/* Content */}
              <div className="p-5">
                <h3 className="font-heading text-xl font-semibold text-foreground mb-2 group-hover:text-primary transition-colors">
                  {deal.title}
                </h3>
                
                <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
                  <Icon name="MapPinIcon" size={16} />
                  <span>{deal.destination}</span>
                </div>

                <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                  {deal.description}
                </p>

                {/* Highlights */}
                <div className="flex flex-wrap gap-2 mb-4">
                  {deal.highlights.slice(0, 2).map((highlight, index) =>
                <span
                  key={index}
                  className="px-2 py-1 bg-surface text-xs text-foreground rounded">

                      {highlight.length > 20 ? highlight.substring(0, 20) + '...' : highlight}
                    </span>
                )}
                </div>

                {/* Confidence Score */}
                {deal.aiGenerated && deal.confidence > 0.7 &&
              <div className="mb-4 flex items-center gap-2">
                    <div className="flex-1 h-1.5 bg-surface rounded-full overflow-hidden">
                      <div
                    className="h-full bg-gradient-to-r from-primary to-secondary rounded-full"
                    style={{ width: `${deal.confidence * 100}%` }} />

                    </div>
                    <span className="text-xs text-muted-foreground">
                      {Math.round(deal.confidence * 100)}% match
                    </span>
                  </div>
              }

                {/* Price & CTA */}
                <div className="flex items-center justify-between pt-4 border-t border-border">
                  <div>
                    {deal.originalPrice &&
                  <p className="text-xs text-muted-foreground line-through">
                        £{deal.originalPrice.toLocaleString()}
                      </p>
                  }
                    <p className="font-heading text-2xl font-bold text-primary">
                      £{deal.price.toLocaleString()}
                    </p>
                  </div>
                  <Link
                  href={deal.ctaLink}
                  className="px-4 py-2 bg-coral text-white text-sm font-semibold rounded-lg hover:bg-[#FF5722] transition-colors duration-300">

                    {deal.ctaText}
                  </Link>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* View All Button */}
        <div className="text-center">
          <Link
            href="/flight-search"
            className="inline-flex items-center gap-2 px-8 py-3 bg-primary text-white font-heading font-semibold rounded-lg hover:bg-secondary transition-colors duration-300">

            <span>Discover More Personalized Deals</span>
            <Icon name="ArrowRightIcon" size={20} />
          </Link>
        </div>
      </div>
    </section>);

};

export default PersonalizedDeals;